import { Component, forwardRef, OnDestroy, OnInit, input } from '@angular/core';
import {
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { Schema } from '../../../../../core/_models/schema';
import { SchemaTranslations } from '../../../../../shared/models/schema-translations';
import { AbstractControlValueAccessor } from '../../../../../shared/abstract_control_value_accessor.abstract';
import { Subscription } from 'rxjs';

import { PropertyIsFieldRequiredPipe } from '@app/+profile-form/components/properties-form/property-is-field-required.pipe';
import { ShowPropertyArrayHintPipe } from '@app/+profile-form/components/properties-form/property-array/show-property-array-hint.pipe';
import { PropertyArrayHintTextPipe } from '@app/+profile-form/components/properties-form/property-array/property-array-hint-text.pipe';
import { TranslatePipe } from '@ngx-translate/core';
import { PropertyGetFieldsPipe } from '@app/+profile-form/components/properties-form/property-array/property-get-fields.pipe';
import { HintComponent } from '@app/+profile-form/components/properties-form/property-array/hint/hint.component';
import { PropertyGetEnumValuesPipe } from '@app/+profile-form/components/properties-form/property-array/property-get-enum-values.pipe';
import { PropertyHasNumericValuesPipe } from '@app/+profile-form/components/properties-form/property-array/property-has-numeric-values.pipe';
import { ShowPropertyArraySelectAllButtonPipe } from '@app/+profile-form/components/properties-form/property-array/show-property-array-select-all-button.pipe';

@Component({
  selector: 'app-property-array-fixed',
  templateUrl: './property-array-fixed.component.html',
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => PropertyArrayFixedComponent), multi: true },
    { provide: NG_VALIDATORS, useExisting: forwardRef(() => PropertyArrayFixedComponent), multi: true },
  ],
  imports: [
    ReactiveFormsModule,
    PropertyIsFieldRequiredPipe,
    ShowPropertyArrayHintPipe,
    PropertyArrayHintTextPipe,
    TranslatePipe,
    PropertyGetFieldsPipe,
    HintComponent,
    PropertyGetEnumValuesPipe,
    PropertyHasNumericValuesPipe,
    ShowPropertyArraySelectAllButtonPipe
],
})
export class PropertyArrayFixedComponent extends AbstractControlValueAccessor implements OnInit, OnDestroy, Validator {
  public readonly controlName = input<string>(undefined!);
  public readonly properties = input<Schema>(undefined!);
  public readonly schemaTranslations = input<SchemaTranslations | null>(undefined!);
  public readonly errors = input<any>();
  public formGroup = new UntypedFormGroup({});
  private formSub: Subscription;

  public ngOnInit(): void {
    this.formSub = this.formGroup.valueChanges.subscribe(() => this.formControlChange());
    this.initializeProperty();
  }

  public ngOnDestroy(): void {
    this.formSub.unsubscribe();
  }

  public writeValue(obj: any): void {
    if (obj) { obj.forEach((enumValue) => this.formGroup.controls[enumValue]?.patchValue(true)); }
  }

  public validate(): ValidationErrors | any {
    return this.formGroup.errors;
  }

  protected selectAll(): void {
    Object.keys(this.formGroup.controls).forEach(key =>
      this.formGroup.controls[key].patchValue(true),
    );
  }

  private formControlChange(): void {
    this.onTouched();

    const propertyValues: any[] = [];
    Object.keys(this.formGroup.value).forEach((enumValue) => {
      if (this.formGroup.value[enumValue]) {
        propertyValues.push(this.properties().hasNumericValues(this.controlName()) ? parseInt(enumValue, 10) : enumValue);
      }
    });

    this.onChange(propertyValues);
  }

  private initializeProperty(): void {
    const enumValues = this.properties().getEnumValues(this.controlName()) || [];

    enumValues.forEach((enumValue) => {
      this.formGroup.addControl(enumValue, new UntypedFormControl(false));
    });
  }
}
